// Marketing LP — SEO-focused. Lives at `/`.

import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import {
  BRAND,
  BRAND_SOFT,
  ACCENT,
  PictX,
  PictName,
  ConfettiBurst,
  SectionEyebrow,
  SectionH2,
  useBoominFx,
  useIsMobile,
} from "./shared";
import { OnboardingHero } from "./OnboardingHero";
import {
  RedesignCategory,
  categoryLabel,
  categoryPict,
  couponTag,
  fetchTop,
  inferCategory,
} from "./api";
import { Coupon as ApiCoupon } from "../../Types/App";

const brand = BRAND;
const brandSoft = BRAND_SOFT;
const accent = ACCENT;

// ── Shared data: fetched once and threaded through the LP sections that
// need it. Falls back to an empty array on failure so the marketing copy
// still renders.
type LandingData = {
  loaded: boolean;
  coupons: ApiCoupon[];
};

const useLandingData = (): LandingData => {
  const [state, setState] = useState<LandingData>({ loaded: false, coupons: [] });
  useEffect(() => {
    let cancelled = false;
    fetchTop()
      .then((res) => {
        if (cancelled) return;
        setState({ loaded: true, coupons: res.coupons || [] });
      })
      .catch(() => {
        if (cancelled) return;
        setState({ loaded: true, coupons: [] });
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return state;
};

// city extracted from "東京都・渋谷区" style addressing.
const formatArea = (c: ApiCoupon): string =>
  c.hub_city || c.hub_state || "";

export const LandingPage: React.FC = () => {
  useBoominFx();
  const isMobile = useIsMobile(720);
  const data = useLandingData();

  return (
    <>
      <Helmet>
        <title>いま、このあたりで使えるクーポンが見つかる｜ローカルクーポンのBoomin'（ブーミン）</title>
        <meta
          name="description"
          content="徒歩10分以内で、ちょっとうれしい寄り道が見つかる、ローカルクーポンのウェブサービス。渋谷・新宿エリア対応、順次拡大中。"
        />
        <meta
          property="og:title"
          content="いま、このあたりで使えるクーポンが見つかる｜ローカルクーポンのBoomin'（ブーミン）"
        />
        <meta
          property="og:description"
          content="徒歩10分以内で、ちょっとうれしい寄り道が見つかる、ローカルクーポンのウェブサービス。渋谷・新宿エリア対応、順次拡大中。"
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <div
        data-screen-label="LP"
        style={{
          background: "#FFFBF7",
          width: "100%",
          minHeight: "100%",
          color: "#1A1A1A",
          fontFamily:
            '"Noto Sans JP", "Hiragino Sans", "Hiragino Kaku Gothic ProN", system-ui, sans-serif',
        }}
      >
        <LPNav isMobile={isMobile} />
        <LPHero isMobile={isMobile} />
        <LPLiveBand coupons={data.coupons} />
        <LPHowItWorks isMobile={isMobile} />
        <LPCoupons isMobile={isMobile} coupons={data.coupons} loaded={data.loaded} />
        <LPAreas isMobile={isMobile} coupons={data.coupons} />
        <LPBigCTA isMobile={isMobile} />
        <LPFooter isMobile={isMobile} />
      </div>
    </>
  );
};

// ── Sticky top nav ────────────────────────────────────────────────────────
const LPNav: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 20 : 64;
  return (
    <header
      style={{
        padding: `${isMobile ? 14 : 20}px ${pad}px`,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderBottom: "1px solid #F0E6DC",
        background: "rgba(255,251,247,0.96)",
        backdropFilter: "blur(12px)",
        WebkitBackdropFilter: "blur(12px)",
        position: "sticky",
        top: 0,
        zIndex: 30,
      }}
    >
      <a
        href="/"
        style={{
          fontSize: isMobile ? 20 : 24,
          fontWeight: 800,
          letterSpacing: "-0.01em",
          display: "flex",
          alignItems: "center",
          gap: 8,
          textDecoration: "none",
          color: "#1A1A1A",
        }}
      >
        <span
          style={{
            width: isMobile ? 28 : 32,
            height: isMobile ? 28 : 32,
            borderRadius: 8,
            background: brand,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name="walk" size={isMobile ? 16 : 18} stroke="#fff" strokeWidth={2} />
        </span>
        Boomin<span style={{ color: brand }}>'</span>
      </a>
      {!isMobile && (
        <nav style={{ display: "flex", gap: 32, fontSize: 14, fontWeight: 700 }}>
          {(
            [
              ["エリア", "#areas"],
              ["使い方", "#how"],
              ["クーポン", "#coupons"],
              ["加盟店募集", "/sponsor"],
            ] as [string, string][]
          ).map(([l, h]) => (
            <a key={l} href={h} style={{ color: "#555", textDecoration: "none" }}>
              {l}
            </a>
          ))}
        </nav>
      )}
      <a
        href="/discover"
        style={{
          background: brand,
          color: "#fff",
          textDecoration: "none",
          borderRadius: 999,
          padding: isMobile ? "8px 14px" : "11px 20px",
          fontSize: isMobile ? 12 : 13,
          fontWeight: 800,
          boxShadow: `0 6px 14px ${brand}55`,
          display: "inline-flex",
          alignItems: "center",
          gap: 4,
        }}
      >
        {isMobile ? "探す" : "クーポンを探す"}
        {!isMobile && <PictX name="chevron" size={14} stroke="#fff" strokeWidth={2.4} />}
      </a>
    </header>
  );
};

// ── Hero ──────────────────────────────────────────────────────────────────
const LPHero: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(180deg, ${brandSoft} 0%, #FFFBF7 100%)`,
        padding: `${isMobile ? 48 : 96}px ${pad}px ${isMobile ? 64 : 120}px`,
      }}
    >
      <ConfettiBurst
        density={isMobile ? 18 : 32}
        palette={[brand, accent, "#FFE05A", "#FFC9B0", "#3DBED9"]}
      />

      <div
        style={{
          position: "relative",
          maxWidth: 1280,
          margin: "0 auto",
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: "center",
          gap: isMobile ? 32 : 48,
        }}
      >
        <div style={{ flex: "1 1 0%", minWidth: 0 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              background: "#fff",
              padding: "7px 14px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 800,
              color: brand,
              boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
              border: `1px solid ${brand}22`,
              marginBottom: 24,
            }}
          >
            <PictX name="pin" size={14} stroke={brand} strokeWidth={2} />
            渋谷・新宿エリア対応 ・ 順次拡大中
          </div>
          <h1
            style={{
              fontSize: isMobile ? 44 : 80,
              fontWeight: 800,
              lineHeight: 1.08,
              margin: 0,
              letterSpacing: "-0.01em",
              color: "#1A1A1A",
            }}
          >
            寄り道、
            <br />
            <span
              style={{
                background: `linear-gradient(transparent 65%, ${brand}30 65%)`,
                paddingRight: isMobile ? 6 : 12,
              }}
            >
              はじめよう
            </span>
            <span style={{ color: brand }}>。</span>
          </h1>
          <p
            style={{
              marginTop: isMobile ? 20 : 28,
              fontSize: isMobile ? 15 : 19,
              color: "#444",
              lineHeight: 1.75,
              maxWidth: 540,
            }}
          >
            いま、このあたりで使える クーポン。
            <br />
            徒歩10分以内で、ちょっとうれしい寄り道が見つかります。
          </p>

          <div
            style={{
              marginTop: isMobile ? 28 : 40,
              display: "flex",
              gap: 12,
              flexWrap: "wrap",
            }}
          >
            <a
              href="/discover"
              style={{
                background: brand,
                color: "#fff",
                textDecoration: "none",
                borderRadius: 999,
                padding: isMobile ? "14px 22px" : "18px 28px",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 800,
                boxShadow: `0 12px 24px ${brand}55`,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              今すぐ探す
              <PictX name="chevron" size={16} stroke="#fff" strokeWidth={2.4} />
            </a>
            <a
              href="#how"
              style={{
                background: "#fff",
                color: "#1A1A1A",
                textDecoration: "none",
                border: "1.5px solid rgba(0,0,0,0.12)",
                borderRadius: 999,
                padding: isMobile ? "14px 22px" : "18px 28px",
                fontSize: isMobile ? 14 : 16,
                fontWeight: 700,
              }}
            >
              使い方を見る
            </a>
          </div>

          <div
            style={{
              marginTop: 22,
              fontSize: 12,
              color: "#666",
              display: "flex",
              alignItems: "center",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {["登録不要", "無料", "ブラウザで完結"].map((l) => (
              <span
                key={l}
                style={{ display: "inline-flex", alignItems: "center", gap: 4 }}
              >
                <PictX name="check" size={14} stroke="#0FAC6D" strokeWidth={2.6} />
                {l}
              </span>
            ))}
          </div>
        </div>

        <div
          style={{
            flex: isMobile ? "0 0 auto" : "0 0 420px",
            position: "relative",
            height: isMobile ? 280 : 420,
            width: isMobile ? 280 : 420,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div style={{ transform: isMobile ? "scale(1)" : "scale(1.35)" }}>
            <OnboardingHero brand={brand} accent={accent} />
          </div>
        </div>
      </div>
    </section>
  );
};

// ── Live counter band (marquee) ───────────────────────────────────────────
const LPLiveBand: React.FC<{ coupons: ApiCoupon[] }> = ({ coupons }) => {
  // Group coupons by city and sort by count desc so the marquee leads with
  // the busiest area. Falls back to a single "全エリア" entry while data loads.
  const items = useMemo(() => {
    const counts = new Map<string, number>();
    coupons.forEach((c) => {
      const area = c.hub_city || c.hub_state;
      if (!area) return;
      counts.set(area, (counts.get(area) || 0) + 1);
    });
    const arr = Array.from(counts.entries())
      .map(([area, n]) => ({ area, n }))
      .sort((a, b) => b.n - a.n);
    if (arr.length === 0) {
      // Placeholder while top still loading
      return [{ area: "全国", n: coupons.length }];
    }
    // Marquee feels too short with <4 items; pad by repeating leading entries.
    while (arr.length < 6) arr.push(...arr.slice(0, Math.min(arr.length, 6 - arr.length)));
    return arr;
  }, [coupons]);
  return (
    <div
      style={{
        background: "#1A1A1A",
        color: "#fff",
        padding: "16px 0",
        overflow: "hidden",
        whiteSpace: "nowrap",
        position: "relative",
      }}
    >
      <div
        data-boomin-anim
        style={{
          display: "inline-flex",
          gap: 40,
          paddingLeft: 40,
          animation: "lp-marquee 32s linear infinite",
        }}
      >
        {[...items, ...items].map((it, i) => (
          <span
            key={i}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              fontSize: 14,
              fontWeight: 700,
            }}
          >
            <span
              data-boomin-anim
              style={{
                width: 8,
                height: 8,
                borderRadius: 999,
                background: brand,
                boxShadow: `0 0 0 4px ${brand}33`,
                animation: "boom-pulse 1.4s ease-in-out infinite",
              }}
            />
            <span style={{ color: "#fff" }}>{it.area}</span>
            <span style={{ color: brand, fontWeight: 800 }}>{it.n}件</span>
            <span style={{ color: "#888" }}>いま使える</span>
          </span>
        ))}
      </div>
    </div>
  );
};

// ── How it works ──────────────────────────────────────────────────────────
const LPHowItWorks: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  const steps: { n: number; title: string; body: string; icon: PictName }[] = [
    {
      n: 1,
      title: "近くを開く",
      body: "地図 or リストで、いまいる場所のクーポンが一覧に表示されます。",
      icon: "map",
    },
    {
      n: 2,
      title: "気になるお店へ",
      body: "徒歩10分・営業時間・条件をチェックして、サクッと向かいましょう。",
      icon: "walk",
    },
    {
      n: 3,
      title: "お店で見せるだけ",
      body: "会計前に「Boomin'のクーポン」と伝えて、画面のタイマーを提示します。",
      icon: "ticket",
    },
  ];
  return (
    <section
      id="how"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: "#FFFBF7",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionEyebrow>HOW IT WORKS</SectionEyebrow>
        <SectionH2 isMobile={isMobile}>
          使い方は<span style={{ color: brand }}>3ステップ</span>。
        </SectionH2>
        <div
          style={{
            marginTop: isMobile ? 36 : 60,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)",
            gap: isMobile ? 16 : 24,
          }}
        >
          {steps.map((s) => (
            <article
              key={s.n}
              style={{
                position: "relative",
                background: "#fff",
                borderRadius: 22,
                padding: isMobile ? 24 : 32,
                border: "1px solid #F0E6DC",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: isMobile ? 12 : 18,
                  right: isMobile ? 16 : 24,
                  fontSize: isMobile ? 56 : 84,
                  fontWeight: 800,
                  color: brand,
                  opacity: 0.16,
                  lineHeight: 1,
                  fontFamily: '"SF Pro Display", system-ui',
                }}
              >
                {String(s.n).padStart(2, "0")}
              </div>
              <div
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: 14,
                  background: brandSoft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: 16,
                }}
              >
                <PictX name={s.icon} size={26} stroke={brand} strokeWidth={1.9} />
              </div>
              <h3
                style={{
                  fontSize: isMobile ? 18 : 22,
                  fontWeight: 800,
                  margin: 0,
                  marginBottom: 8,
                  letterSpacing: "-0.005em",
                }}
              >
                {s.title}
              </h3>
              <p style={{ fontSize: 13, color: "#666", lineHeight: 1.7, margin: 0 }}>
                {s.body}
              </p>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Popular coupons ───────────────────────────────────────────────────────
type LPCardCoupon = {
  id: number;
  shop: string;
  area: string;
  cat: string;
  icon: PictName;
  title: string;
  img: string;
  tag: string;
};

// Map an API coupon to the LP card shape, picking icon + category label
// from the inferCategory helper that also drives /discover.
const toCardCoupon = (c: ApiCoupon): LPCardCoupon => {
  const cat: RedesignCategory = inferCategory(c, null);
  return {
    id: c.id,
    shop: c.hub_name || "",
    area: formatArea(c),
    cat: categoryLabel(cat),
    icon: categoryPict(cat),
    title: c.title,
    img: c.image || c.hub_image || "",
    tag: couponTag(c.period_end),
  };
};

const LPCoupons: React.FC<{
  isMobile: boolean;
  coupons: ApiCoupon[];
  loaded: boolean;
}> = ({ isMobile, coupons, loaded }) => {
  const pad = isMobile ? 24 : 80;
  // Show the first 4 valid coupons — /top already orders them by recency.
  const cards = useMemo(
    () => coupons.filter((c) => c.title && c.hub_name).slice(0, 4).map(toCardCoupon),
    [coupons]
  );
  return (
    <section
      id="coupons"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: "#fff",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 14,
          }}
        >
          <div>
            <SectionEyebrow>POPULAR COUPONS</SectionEyebrow>
            <SectionH2 isMobile={isMobile}>
              今日のおすすめ<span style={{ color: brand }}>クーポン</span>
            </SectionH2>
          </div>
          <a
            href="/discover"
            style={{
              color: brand,
              fontSize: 13,
              fontWeight: 800,
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
            }}
          >
            すべて見る
            <PictX name="chevron" size={14} stroke={brand} strokeWidth={2.6} />
          </a>
        </div>
        <div
          style={{
            marginTop: isMobile ? 28 : 52,
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, 1fr)",
            gap: isMobile ? 14 : 24,
          }}
        >
          {cards.length > 0
            ? cards.map((c) => <LPCouponCard key={c.id} c={c} isMobile={isMobile} />)
            : loaded
            ? (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "32px 20px",
                    textAlign: "center",
                    color: "#888",
                    border: "1px dashed #F0E6DC",
                    borderRadius: 18,
                  }}
                >
                  公開中のクーポンはありません
                </div>
              )
            : Array.from({ length: 4 }).map((_, i) => (
                <LPCouponCardSkeleton key={i} isMobile={isMobile} />
              ))}
        </div>
      </div>
    </section>
  );
};

const LPCouponCard: React.FC<{ c: LPCardCoupon; isMobile: boolean }> = ({
  c,
  isMobile,
}) => (
  <a
    href={`/c/${c.id}`}
    style={{
      display: "flex",
      background: "#FFFBF7",
      borderRadius: 18,
      overflow: "hidden",
      border: "1px solid #F0E6DC",
      textDecoration: "none",
      color: "inherit",
    }}
  >
    <div
      style={{
        width: isMobile ? 120 : 200,
        flexShrink: 0,
        position: "relative",
        overflow: "hidden",
        background: brandSoft,
      }}
    >
      {c.img ? (
        <img
          src={c.img}
          alt={c.shop}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <PictX name={c.icon} size={48} stroke={brand} strokeWidth={1.4} />
        </div>
      )}
      <div
        style={{
          position: "absolute",
          top: 12,
          left: 12,
          background: brand,
          color: "#fff",
          fontSize: 10,
          fontWeight: 800,
          padding: "4px 10px",
          borderRadius: 999,
          boxShadow: "0 2px 6px rgba(0,0,0,0.18)",
        }}
      >
        {c.tag}
      </div>
    </div>
    <div
      style={{
        flex: 1,
        padding: isMobile ? "14px 16px" : "22px 24px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        minWidth: 0,
      }}
    >
      <div>
        <div
          style={{
            display: "inline-block",
            background: brandSoft,
            color: brand,
            padding: "2px 9px",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 800,
            marginBottom: 8,
          }}
        >
          {c.cat}
        </div>
        <div style={{ fontSize: 11, color: "#888", fontWeight: 600, marginBottom: 4 }}>
          {c.shop}
        </div>
        <h3
          style={{
            fontSize: isMobile ? 15 : 19,
            fontWeight: 800,
            lineHeight: 1.3,
            margin: 0,
            letterSpacing: "-0.005em",
          }}
        >
          {c.title}
        </h3>
      </div>
      {c.area && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            fontSize: 11,
            color: "#666",
            marginTop: 12,
            flexWrap: "wrap",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 3 }}>
            <PictX name="pin" size={11} stroke="#666" strokeWidth={1.9} />
            {c.area}
          </span>
        </div>
      )}
    </div>
  </a>
);

const LPCouponCardSkeleton: React.FC<{ isMobile: boolean }> = ({ isMobile }) => (
  <div
    style={{
      display: "flex",
      background: "#FFFBF7",
      borderRadius: 18,
      overflow: "hidden",
      border: "1px solid #F0E6DC",
      minHeight: isMobile ? 120 : 180,
    }}
  >
    <div
      style={{
        width: isMobile ? 120 : 200,
        flexShrink: 0,
        background: brandSoft,
      }}
    />
    <div style={{ flex: 1, padding: isMobile ? "14px 16px" : "22px 24px" }}>
      <div
        style={{
          width: 60,
          height: 18,
          background: brandSoft,
          borderRadius: 999,
          marginBottom: 12,
        }}
      />
      <div
        style={{
          width: "80%",
          height: 18,
          background: "#F0E6DC",
          borderRadius: 4,
          marginBottom: 8,
        }}
      />
      <div
        style={{
          width: "50%",
          height: 18,
          background: "#F0E6DC",
          borderRadius: 4,
        }}
      />
    </div>
  </div>
);

// ── Areas grid ────────────────────────────────────────────────────────────
const LPAreas: React.FC<{ isMobile: boolean; coupons: ApiCoupon[] }> = ({
  isMobile,
  coupons,
}) => {
  const pad = isMobile ? 24 : 80;
  // Group by city, sort by count, top 6. Each card links to /area/:name, which
  // resolves the name via preset coords or forward-geocode for a focused browse.
  const areas = useMemo(() => {
    const counts = new Map<string, number>();
    coupons.forEach((c) => {
      const name = c.hub_city || c.hub_state;
      if (!name) return;
      counts.set(name, (counts.get(name) || 0) + 1);
    });
    return Array.from(counts.entries())
      .map(([name, n]) => ({ name, n }))
      .sort((a, b) => b.n - a.n)
      .slice(0, 6);
  }, [coupons]);
  if (areas.length === 0) return null;
  return (
    <section
      id="areas"
      style={{
        padding: `${isMobile ? 64 : 120}px ${pad}px`,
        background: brandSoft,
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <SectionEyebrow>EXPLORE BY AREA</SectionEyebrow>
        <SectionH2 isMobile={isMobile}>
          エリアから<span style={{ color: brand }}>探す</span>
        </SectionH2>
        <p
          style={{
            marginTop: 12,
            fontSize: 14,
            color: "#666",
            lineHeight: 1.7,
            maxWidth: 640,
          }}
        >
          いま使えるクーポンを地域ごとに一覧で。
          <br />
          ブックマークしておけば、移動中もサクッと開けます。
        </p>
        <div
          style={{
            marginTop: isMobile ? 28 : 48,
            display: "grid",
            gridTemplateColumns: isMobile ? "repeat(2, 1fr)" : "repeat(3, 1fr)",
            gap: isMobile ? 10 : 16,
          }}
        >
          {areas.map((a) => (
            <a
              key={a.name}
              href={`/area/${encodeURIComponent(a.name)}`}
              style={{
                background: "#fff",
                borderRadius: 18,
                padding: isMobile ? 18 : 24,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                border: "1px solid rgba(0,0,0,0.04)",
                textDecoration: "none",
                color: "inherit",
              }}
            >
              <div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 800 }}>
                  {a.name}
                </div>
                <div
                  style={{
                    fontSize: 11,
                    color: "#888",
                    marginTop: 4,
                    fontWeight: 600,
                  }}
                >
                  <span style={{ color: brand, fontSize: 14, fontWeight: 800 }}>
                    {a.n}
                  </span>{" "}
                  件のクーポン
                </div>
              </div>
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 999,
                  background: brandSoft,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PictX name="chevron" size={16} stroke={brand} strokeWidth={2.4} />
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

// ── Big closing CTA ───────────────────────────────────────────────────────
const LPBigCTA: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <section
      style={{
        position: "relative",
        overflow: "hidden",
        padding: `${isMobile ? 80 : 140}px ${pad}px`,
        background: `linear-gradient(135deg, ${brand} 0%, ${accent} 100%)`,
      }}
    >
      <ConfettiBurst density={30} palette={["#fff", "#FFE05A", "#FFC9B0", "#3DBED9"]} />
      <div
        style={{
          position: "relative",
          maxWidth: 800,
          margin: "0 auto",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontSize: isMobile ? 36 : 64,
            fontWeight: 800,
            lineHeight: 1.15,
            color: "#fff",
            margin: 0,
            letterSpacing: "-0.01em",
          }}
        >
          今すぐ、
          <br />
          寄り道をはじめよう。
        </h2>
        <p
          style={{
            marginTop: isMobile ? 18 : 28,
            fontSize: isMobile ? 14 : 17,
            color: "rgba(255,255,255,0.92)",
            lineHeight: 1.7,
          }}
        >
          いま、このあたりで使えるクーポンが見つかります。
        </p>
        <a
          href="/discover"
          style={{
            marginTop: isMobile ? 32 : 44,
            background: "#fff",
            color: brand,
            textDecoration: "none",
            borderRadius: 999,
            padding: isMobile ? "16px 28px" : "20px 36px",
            fontSize: isMobile ? 15 : 18,
            fontWeight: 800,
            boxShadow: "0 16px 32px rgba(0,0,0,0.2)",
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
          }}
        >
          クーポンを探す
          <PictX name="chevron" size={18} stroke={brand} strokeWidth={2.6} />
        </a>
      </div>
    </section>
  );
};

// ── Footer ────────────────────────────────────────────────────────────────
const LPFooter: React.FC<{ isMobile: boolean }> = ({ isMobile }) => {
  const pad = isMobile ? 24 : 80;
  return (
    <footer
      style={{
        padding: `${isMobile ? 48 : 60}px ${pad}px`,
        background: "#1A1A1A",
        color: "#aaa",
      }}
    >
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "1fr 1fr"
              : "minmax(220px, 1fr) repeat(3, 1fr)",
            gap: isMobile ? 28 : 40,
          }}
        >
          <div style={{ gridColumn: isMobile ? "span 2" : "auto" }}>
            <div
              style={{
                fontSize: 22,
                fontWeight: 800,
                color: "#fff",
                marginBottom: 12,
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}
            >
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: 8,
                  background: brand,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PictX name="walk" size={16} stroke="#fff" strokeWidth={2} />
              </span>
              Boomin<span style={{ color: brand }}>'</span>
            </div>
            <p style={{ fontSize: 12, lineHeight: 1.7, margin: 0, maxWidth: 280 }}>
              いま、このあたりで使えるクーポンが見つかる、寄り道のためのウェブサービス。
            </p>
          </div>
          <LPFooterCol
            title="エリア"
            links={[
              ["渋谷区", "/area/渋谷区"],
              ["新宿区", "/area/新宿区"],
              ["千駄ヶ谷", "/area/千駄ヶ谷"],
              ["恵比寿", "/area/恵比寿"],
              ["原宿", "/area/原宿"],
            ]}
          />
          <LPFooterCol
            title="カテゴリ"
            links={[
              ["カフェ", "/discover/cafe"],
              ["ごはん", "/discover/meal"],
              ["スイーツ", "/discover/sweet"],
              ["バー", "/discover/bar"],
              ["ショップ", "/discover/shop"],
            ]}
          />
          <LPFooterCol
            title="サービス"
            links={[
              ["クーポンを探す", "/discover"],
              ["使い方", "/#how"],
              ["加盟店募集", "/sponsor"],
            ]}
          />
        </div>
        <div
          style={{
            marginTop: isMobile ? 32 : 48,
            paddingTop: 24,
            borderTop: "1px solid #333",
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            justifyContent: "space-between",
            gap: 8,
            fontSize: 11,
          }}
        >
          <div>© 2026 Rebase, Inc. All rights reserved.</div>
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <a href="/sponsor" style={{ color: "#aaa", textDecoration: "none" }}>
              クーポンを掲載する
            </a>
            <a
              href="https://www.rebase.co.jp/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              運営会社
            </a>
            <a
              href="https://storage.instabase.jp/boomin_term.pdf"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              利用規約
            </a>
            <a
              href="https://www.rebase.co.jp/privacy_policy/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#aaa", textDecoration: "none" }}
            >
              プライバシーポリシー
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

const LPFooterCol: React.FC<{ title: string; links: [string, string][] }> = ({
  title,
  links,
}) => (
  <div>
    <div
      style={{
        fontSize: 11,
        fontWeight: 800,
        color: "#fff",
        letterSpacing: "0.14em",
        marginBottom: 14,
      }}
    >
      {title.toUpperCase()}
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {links.map(([label, href]) => (
        <a
          key={label}
          href={href}
          style={{ color: "#aaa", fontSize: 13, textDecoration: "none" }}
        >
          {label}
        </a>
      ))}
    </div>
  </div>
);

export default LandingPage;
